export function formatDateTime(dateStr: string | undefined | null): string {
  if (!dateStr || dateStr === '-') return '-';
  try {
    const bj = extractBeijingTime(dateStr);
    if (!bj) return '-';
    return `${bj.year}-${bj.month}-${bj.day} ${bj.hour}:${bj.minute}`;
  } catch {
    return '-';
  }
}

export function formatDate(dateStr: string | undefined | null): string {
  if (!dateStr || dateStr === '-') return '-';
  try {
    const bj = extractBeijingTime(dateStr);
    if (!bj) return '-';
    return `${bj.year}-${bj.month}-${bj.day}`;
  } catch {
    return '-';
  }
}

export function formatDateTimeWithSeconds(dateStr: string | undefined | null): string {
  if (!dateStr || dateStr === '-') return '-';
  try {
    const bj = extractBeijingTime(dateStr);
    if (!bj) return '-';
    return `${bj.year}-${bj.month}-${bj.day} ${bj.hour}:${bj.minute}:${bj.second}`;
  } catch {
    return '-';
  }
}

interface BeijingTimeParts {
  year: string;
  month: string;
  day: string;
  hour: string;
  minute: string;
  second: string;
}

export function extractBeijingTime(dateStr: string): BeijingTimeParts | null {
  // 处理带时区的ISO格式 (如 2026-05-18T12:19:52+08:00 或 2026-05-18T12:19:52Z)
  if (dateStr.includes('T') && (dateStr.includes('+') || dateStr.includes('Z'))) {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return null;
    // 已经是带时区的格式，直接转换为北京时间
    const bjDate = new Date(d.getTime() + 8 * 60 * 60 * 1000);
    return formatBJDate(bjDate);
  }
  // 处理PostgreSQL带时区格式: 2026-05-18 12:19:52+08 或 2026-05-18 12:19:52.123+08
  // 这种格式已经包含时区信息(+08表示UTC+8)，不需要再加8小时
  const matchWithTZ = dateStr.match(/^(\d{4})[-/](\d{1,2})[-/](\d{1,2})[T ](\d{1,2}):(\d{1,2}):(\d{1,2})(?:\.(\d+))?([+-]\d{1,2}(?::?\d{2})?)$/);
  if (matchWithTZ) {
    const tzOffset = matchWithTZ[8];
    // 解析时区偏移量（如 +08, +08:00, -05）
    const tzSign = tzOffset.startsWith('-') ? -1 : 1;
    const tzParts = tzOffset.replace(/^[+-]/, '').split(':');
    const tzHours = parseInt(tzParts[0], 10);
    const tzMinutes = tzParts.length > 1 ? parseInt(tzParts[1], 10) : 0;
    const tzOffsetMs = tzSign * (tzHours * 60 + tzMinutes) * 60 * 1000;

    // 构造带时区的Date对象
    const utcDate = new Date(
      parseInt(matchWithTZ[1], 10),
      parseInt(matchWithTZ[2], 10) - 1,
      parseInt(matchWithTZ[3], 10),
      parseInt(matchWithTZ[4], 10),
      parseInt(matchWithTZ[5], 10),
      parseInt(matchWithTZ[6], 10)
    );
    // 减去时区偏移得到UTC时间，再加8小时得到北京时间
    const bjDate = new Date(utcDate.getTime() - tzOffsetMs + 8 * 60 * 60 * 1000);
    return formatBJDate(bjDate);
  }
  // 处理PostgreSQL不带时区格式: 2026-05-18 12:19:52
  // 数据库存储的是UTC时间，需要+8小时转换为北京时间
  // 使用 new Date() 做精确计算，避免手动跨月处理 bug
  const match = dateStr.match(/^(\d{4})[-/](\d{1,2})[-/](\d{1,2})[T ](\d{1,2}):(\d{1,2}):(\d{1,2})/);
  if (match) {
    // 构造 UTC Date 对象，然后加8小时得到北京时间
    const utcDate = new Date(
      parseInt(match[1], 10),
      parseInt(match[2], 10) - 1,
      parseInt(match[3], 10),
      parseInt(match[4], 10),
      parseInt(match[5], 10),
      parseInt(match[6], 10)
    );
    const bjDate = new Date(utcDate.getTime() + 8 * 60 * 60 * 1000);
    return formatBJDate(bjDate);
  }
  const dateOnly = dateStr.match(/^(\d{4})[-/](\d{1,2})[-/](\d{1,2})$/);
  if (dateOnly) {
    return {
      year: dateOnly[1],
      month: dateOnly[2].padStart(2, '0'),
      day: dateOnly[3].padStart(2, '0'),
      hour: '00',
      minute: '00',
      second: '00',
    };
  }
  return null;
}

function formatBJDate(bjDate: Date): BeijingTimeParts {
  return {
    year: String(bjDate.getUTCFullYear()),
    month: String(bjDate.getUTCMonth() + 1).padStart(2, '0'),
    day: String(bjDate.getUTCDate()).padStart(2, '0'),
    hour: String(bjDate.getUTCHours()).padStart(2, '0'),
    minute: String(bjDate.getUTCMinutes()).padStart(2, '0'),
    second: String(bjDate.getUTCSeconds()).padStart(2, '0'),
  };
}

export function toLocalDateString(date: Date): string {
  const bjDate = new Date(date.getTime() + 8 * 60 * 60 * 1000);
  return `${bjDate.getUTCFullYear()}-${String(bjDate.getUTCMonth() + 1).padStart(2, '0')}-${String(bjDate.getUTCDate()).padStart(2, '0')}`;
}

export function toLocalDateTimeString(date: Date): string {
  const bjDate = new Date(date.getTime() + 8 * 60 * 60 * 1000);
  return `${bjDate.getUTCFullYear()}-${String(bjDate.getUTCMonth() + 1).padStart(2, '0')}-${String(bjDate.getUTCDate()).padStart(2, '0')}T${String(bjDate.getUTCHours()).padStart(2, '0')}:${String(bjDate.getUTCMinutes()).padStart(2, '0')}`;
}

/** 获取当前北京时间日期 (YYYY-MM-DD) */
export function getBeijingToday(): string {
  const now = new Date();
  const bjDate = new Date(now.getTime() + 8 * 60 * 60 * 1000);
  return bjDate.toISOString().split('T')[0];
}

export function formatDuration(seconds: number): string {
  if (!seconds || seconds <= 0) return '0秒';
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;

  if (hours > 0) {
    return `${hours}小时${minutes}分${secs}秒`;
  } else if (minutes > 0) {
    return `${minutes}分${secs}秒`;
  } else {
    return `${secs}秒`;
  }
}
